import React, { createContext, useState, useEffect } from "react";

/**
 * Wishlist Context
 * Manages wishlist state with localStorage persistence
 * Wishlist items are lots (not individual slabs)
 */
export const WishlistContext = createContext({
  wishlist: [],
  wishlistCount: 0,
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  isInWishlist: () => false,
  clearWishlist: () => {}
});

const WISHLIST_STORAGE_KEY = "aurevia_wishlist";

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(WISHLIST_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setWishlist(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error("Failed to load wishlist from localStorage:", error);
      setWishlist([]);
    }
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
    } catch (error) {
      console.error("Failed to save wishlist to localStorage:", error);
    }
  }, [wishlist]);

  // Add lot to wishlist
  const addToWishlist = (lot) => {
    setWishlist((prev) => {
      // Prevent duplicates
      if (prev.some((item) => item.lotId === lot.lotId)) {
        return prev;
      }
      return [...prev, lot];
    });
  };

  // Remove lot from wishlist
  const removeFromWishlist = (lotId) => {
    setWishlist((prev) => prev.filter((item) => item.lotId !== lotId));
  };

  // Check if lot is in wishlist
  const isInWishlist = (lotId) => {
    return wishlist.some((item) => item.lotId === lotId);
  };

  // Clear entire wishlist
  const clearWishlist = () => {
    setWishlist([]);
  };

  const value = {
    wishlist,
    wishlistCount: wishlist.length,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    clearWishlist
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
